import { Component } from '@theme/component';

const HOVER_OPEN_DELAY_MS = 200;
const HOVER_SWITCH_DELAY_MS = 75;

/**
 * Manages the desktop header menu with a small hover-intent delay.
 * CSS owns panel visibility; JavaScript only synchronizes active and ARIA state.
 */
class HeaderMenu extends Component {
  /** @type {{ activeItem: HTMLElement | null }} */
  #state = { activeItem: null };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverActivationTimer;

  /** @type {HTMLElement | null} */
  #pendingHoverItem = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerdown', this.#cancelPendingHoverActivation, { capture: true });
    // Use a native listener so descendant pointerleave events are not delegated to the menu boundary.
    this.addEventListener('pointerleave', this.#deactivate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#cancelPendingHoverActivation, true);
    this.removeEventListener('pointerleave', this.#deactivate);
    this.#cancelPendingHoverActivation();
  }

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    if (!findSubmenu(item)) {
      this.#cancelPendingHoverActivation();
      this.#deactivate();
      return;
    }

    if (event instanceof PointerEvent) {
      this.#queueHoverActivation(item);
      return;
    }

    this.#cancelPendingHoverActivation();
    this.#activateItem(item);
  };

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (this.#pendingHoverItem === item) this.#cancelPendingHoverActivation();

    const relatedTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    const submenu = findSubmenu(this.#state.activeItem);

    if (relatedTarget?.closest('header-menu .menu-list__list-item') || submenu?.contains(relatedTarget)) return;

    this.#deactivate();
  }

  /** @param {HTMLElement} item */
  #queueHoverActivation(item) {
    this.#cancelPendingHoverActivation();
    this.#pendingHoverItem = item;

    const delay = this.#state.activeItem ? HOVER_SWITCH_DELAY_MS : HOVER_OPEN_DELAY_MS;
    this.#hoverActivationTimer = setTimeout(() => {
      this.#hoverActivationTimer = undefined;
      const listItem = item.closest('.menu-list__list-item');

      if (this.#pendingHoverItem !== item || !listItem?.matches(':hover')) {
        this.#pendingHoverItem = null;
        return;
      }

      this.#pendingHoverItem = null;
      this.#activateItem(item);
    }, delay);
  }

  /** @param {HTMLElement} item */
  #activateItem(item) {
    const previousItem = this.#state.activeItem;
    if (previousItem) {
      previousItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previousItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);
    if (submenu) submenu.dataset.active = '';
  }

  #deactivate = () => {
    this.#cancelPendingHoverActivation();

    const item = this.#state.activeItem;
    if (!item) return;

    const submenu = findSubmenu(item);
    if (submenu) delete submenu.dataset.active;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
  };

  #cancelPendingHoverActivation = () => {
    clearTimeout(this.#hoverActivationTimer);
    this.#hoverActivationTimer = undefined;
    this.#pendingHoverItem = null;
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const menuItem = element.matches('[ref="menuitem"]')
    ? element
    : element.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');

  return menuItem instanceof HTMLElement ? menuItem : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
